import { AnimatePresence, motion } from "framer-motion";
import { MdOpenInFull } from "react-icons/md";
import { LoadingDots } from "utils/loadingAnimation";

interface CodeImageProps {
  currentStepImage: string;
  fullCodeImage: string;
  isLoadingImages: boolean;
  setShowCodeFullscreen: React.Dispatch<React.SetStateAction<boolean>>;
  setInitialCodeTab: React.Dispatch<React.SetStateAction<number>>;
}

const CodeImage: React.FC<CodeImageProps> = ({
  currentStepImage,
  fullCodeImage,
  isLoadingImages,
  setShowCodeFullscreen,
  setInitialCodeTab,
}) => {
  /**
   * Function used when clicking over the code image
   */
  const handleCodeImageClick = () => {
    // Check if it's loading to prevent the click action
    if (isLoadingImages) return;

    // Open the fullscreen mode
    setShowCodeFullscreen(true);

    // And specify the first tab (current step)
    setInitialCodeTab(0);
  };

  return (
    <AnimatePresence initial={false}>
      {(currentStepImage || fullCodeImage || isLoadingImages) && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 174 }}
          exit={{ opacity: 0, height: 0 }}
          transition={{duration: 0.3, ease: "easeOut"}}
          className="group min-h-[174px] h-[174px] rounded-lg relative border cursor-pointer inline-flex justify-center items-center"
          onClick={handleCodeImageClick}
        >
          <div className="absolute bottom-0 left-0 h-10 p-1 bg-neutral-700/30 group-hover:scale-[1.15] ease-out duration-150 origin-bottom-left rounded-tr rounded-bl-lg border-l border-b border-white backdrop-blur-sm justify-start items-start gap-2 inline-flex">
            <div className="w-8 h-8 relative">
              <MdOpenInFull className="w-full h-full fill-white" />
            </div>
          </div>
          <AnimatePresence mode="wait">
            {isLoadingImages ? (
              <motion.div
                key="loading-code-image"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full h-[174px] inline-flex justify-center items-center"
              >
                <LoadingDots bgColor="bg-secondary-600" />
              </motion.div>
            ) : (
              <motion.img
                key="code-image"
                className="max-h-[174px] w-auto object-contain"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                src={currentStepImage ? currentStepImage : fullCodeImage}
              />
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CodeImage;
